import styled from 'styled-components'
import isEmpty from 'lodash/isEmpty'
import { Body } from '@shipt/design-system-typography'
import { BACKUP_PREFERENCE_SELECTION } from '@/features/shared/constants/global'
import { type RetrievedPreference } from '@/features/shop/services/Substitutions/types'
import { getIsProductPreference } from '@/features/shop/services/Substitutions/utils'
import { isButtonActionEvent } from '@/features/shared/utils/keyEvents'
import { ChevronRightIcon } from '@shipt/design-system-icons'

type Props = {
  note: string
  backup: RetrievedPreference | undefined
  handleOpenModal: () => void
  isInstructionsEditable?: boolean
  isOrderModal?: boolean
}

export const ProductInstructions = ({
  note = '',
  backup,
  handleOpenModal,
  isInstructionsEditable = true,
  isOrderModal = false,
}: Props) => {
  if (!note && isEmpty(backup)) return null

  const handleOnKeyPress = (e: React.KeyboardEvent) => {
    if (isButtonActionEvent(e) && isInstructionsEditable) {
      e.preventDefault()
      handleOpenModal()
    }
  }

  const backupText = getIsProductPreference(backup)
    ? `Backup: ${backup.name}`
    : `Backup: ${BACKUP_PREFERENCE_SELECTION.DO_NOT_SUBSTITUTE}`

  const getAriaLabel = () => {
    if (note && !isEmpty(backup)) return `${backupText} and note: ${note}`
    if (note) return `Note: ${note}`
    return backupText
  }

  return (
    <Wrapper
      tabIndex={0}
      onKeyPress={handleOnKeyPress}
      onClick={() => isInstructionsEditable && handleOpenModal()}
      isInstructionsEditable={isInstructionsEditable}
      aria-label={getAriaLabel()}
      role="button"
    >
      <InstructionsDetails>
        {!isEmpty(backup) && (
          <InstructionsText size={isOrderModal ? 'sm' : 'lg'}>{backupText}</InstructionsText>
        )}
        {!!note && (
          <InstructionsText size={isOrderModal ? 'sm' : 'lg'}>Note: {note}</InstructionsText>
        )}
      </InstructionsDetails>
      {!!isInstructionsEditable && <ChevronRight />}
    </Wrapper>
  )
}

const Wrapper = styled.div<{ isInstructionsEditable: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.gray50};
  border-radius: 0.25rem;
  padding: 1rem;
  width: 100%;
  border: none;
  text-align: left;
  cursor: ${(p) => p.isInstructionsEditable && 'pointer'};
  min-width: 0;
`

const InstructionsText = styled(Body)`
  padding-top: 4px;
  padding-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const InstructionsDetails = styled.div`
  min-width: 0;
`
const ChevronRight = styled(ChevronRightIcon)`
  min-width: 1.5rem;
`
